import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useAtomValue } from 'jotai';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { EmptyTab, getEmptyTabProps } from '@/components/EmptyTab';
import { InboxAnnouncementBanner } from '@/components/inbox/InboxAnnouncementBanner';
import { InboxRowShell } from '@/components/inbox/InboxRowShell';
import { InquiryCard } from '@/components/inbox/InquiryCard';
import { MessageCardSkeleton } from '@/components/inbox/MessageCardSkeleton';
import { QuestionGroupCard } from '@/components/inbox/QuestionGroupCard';
import { LOAD_MORE_SCROLL_THRESHOLD } from '@/components/inbox/constants';
import { type DesktopInquiriesListViewProps, type TabRoute } from '@/components/inbox/types';
import { SupportThreadCard } from '@/components/supportThread/SupportThreadCard';
import { Divider } from '@/components/ui/divider';
import { FlatList } from '@/components/ui/flat-list';
import { RefreshControl } from '@/components/ui/refresh-control';
import { Spinner } from '@/components/ui/spinner';
import { VStack } from '@/components/ui/vstack';
import { INBOX_ANNOUNCEMENT_BANNER } from '@/constants/inboxFeatureBanners';
import { useClosedTabAutoPagination } from '@/hooks/inbox/useClosedTabAutoPagination';
import { useFillGroupedInboxList } from '@/hooks/inbox/useFillGroupedInboxList';
import {
  useAnonymousInquiryGroupingEnabled,
  useGroupedInquiriesEnabled,
} from '@/hooks/inbox/useGroupedInquiriesEnabled';
import { useInboxAnnouncementBanner } from '@/hooks/inbox/useInboxAnnouncementBanner';
import {
  useInboxQuestionGroupCardsEnabled,
  useInboxQuestionGroups,
} from '@/hooks/inbox/useInboxQuestionGroups';
import { selectedCancerFocusAtom, tabSelectedKeyAtom, userIdAtom } from '@/services/globalState';
import {
  buildInboxRows,
  getFirstMultiThreadQuestionGroupKey,
  type InboxRow,
} from '@/util/inboxRows';
import {
  buildInboxInquiryGroups,
  filterClosedTabInquiriesForUser,
  filterOpenTabInquiriesForUser,
  getFirstTabConversationId,
  getInquiryGroupExperts,
  getInquiryGroupKey,
  type InquiryGroupStateCounts,
  getInquiryGroupStateCounts,
  getInquiryConversationIds,
  getUniqueInquiryLabels,
} from '@/util/inquiryGroups';
import { Sentry } from '@/util/sentry';
import { getModRouteLabelsForGroup } from '@/utils/modHelpers';

/** Module level so the list sees one separator type across renders instead of a new one each time. */
function FlatListDivider() {
  return <Divider className="bg-background-100" />;
}

const noMorePages = () => Promise.resolve();

export const DesktopInquiriesListView = memo(function DesktopInquiriesListView({
  inquiryCancerTypesData,
  openInquiries,
  closedInquiries,
  allInquiries,
}: DesktopInquiriesListViewProps) {
  const tabSelected = useAtomValue(tabSelectedKeyAtom);

  const selectedCancerFocus = useAtomValue(selectedCancerFocusAtom);

  const userId = useAtomValue(userIdAtom);

  const isInboxGroupedInquiriesEnabled = useGroupedInquiriesEnabled();

  const isAnonymousInquiryGroupingEnabled = useAnonymousInquiryGroupingEnabled();

  const flatListRef = useRef<FlatList>(null);

  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    flatListRef?.current?.scrollToOffset({ animated: true, offset: 0 });
  }, [selectedCancerFocus, tabSelected]);

  const isLoading =
    (tabSelected === 'open' && openInquiries.isLoading) ||
    (tabSelected === 'closed' && closedInquiries.isLoading) ||
    (tabSelected === 'all' && allInquiries.isLoading);

  const openInquiriesRawList =
    openInquiries.data?.pages?.flatMap((page) => page?.inquiries ?? []) ?? [];

  /** TODO: Remove filterOpenTabInquiriesForUser — redundant now that the API
   * filters server-side via conversationState: 'active', active + inactive-for-author. */
  const openInquiriesList = filterOpenTabInquiriesForUser(openInquiriesRawList, userId);

  const closedInquiriesRawList =
    closedInquiries.data?.pages?.flatMap((page) => page?.inquiries ?? []) ?? [];

  const closedInquiriesList = filterClosedTabInquiriesForUser(closedInquiriesRawList, userId);

  const allInquiriesList = allInquiries.data?.pages?.flatMap((page) => page?.inquiries ?? []) ?? [];

  const tabToItemsMap: Record<TabRoute['key'], typeof openInquiriesList> = {
    open: openInquiriesList,
    closed: closedInquiriesList,
    all: allInquiriesList,
    drafts: [],
  };

  const inquiries = tabToItemsMap[tabSelected];

  const tabToPagesMap: Record<TabRoute['key'], typeof allInquiries.data> = {
    open: openInquiries.data,
    closed: closedInquiries.data,
    all: allInquiries.data,
    drafts: undefined,
  };

  const tabPages = tabToPagesMap[tabSelected]?.pages;

  useClosedTabAutoPagination({
    enabled: tabSelected === 'closed',
    visibleCount: closedInquiriesList.length,
    hasNextPage: closedInquiries.hasNextPage,
    isLoading: closedInquiries.isLoading,
    isFetching: closedInquiries.isFetching,
    isFetchingNextPage: closedInquiries.isFetchingNextPage,
    fetchNextPage: closedInquiries.fetchNextPage,
  });

  const sourceInquiriesForBreakdown: typeof inquiries = (() => {
    if (tabSelected === 'open') {
      return openInquiriesRawList;
    }

    if (tabSelected === 'closed') {
      return closedInquiriesRawList;
    }

    if (tabSelected === 'all') {
      return allInquiriesList;
    }

    return [];
  })();

  const fullGroupSourceInquiries = allInquiriesList.length
    ? allInquiriesList
    : sourceInquiriesForBreakdown;

  const fullGroupInquiriesByKey = useMemo(() => {
    const grouped = new Map<string, typeof fullGroupSourceInquiries>();

    fullGroupSourceInquiries.forEach((inquiry) => {
      const key = getInquiryGroupKey(inquiry);

      const existing = grouped.get(key);

      if (existing) {
        existing.push(inquiry);

        return;
      }

      grouped.set(key, [inquiry]);
    });

    return grouped;
  }, [fullGroupSourceInquiries]);

  const groupStateBreakdownByKey = useMemo(() => {
    const grouped = new Map<string, typeof fullGroupSourceInquiries>();

    fullGroupSourceInquiries.forEach((inquiry) => {
      const key = getInquiryGroupKey(inquiry);

      const existing = grouped.get(key);

      if (existing) {
        existing.push(inquiry);

        return;
      }

      grouped.set(key, [inquiry]);
    });

    const breakdownByKey = new Map<string, InquiryGroupStateCounts>();

    grouped.forEach((groupInquiries, key) => {
      const breakdown = getInquiryGroupStateCounts(groupInquiries);

      if (breakdown) {
        breakdownByKey.set(key, breakdown);
      }
    });

    return breakdownByKey;
  }, [fullGroupSourceInquiries]);

  const isCardList = useInboxQuestionGroupCardsEnabled();

  const { questionGroups, remaining } = useInboxQuestionGroups(inquiries, tabPages);

  const groups = useMemo(() => {
    return buildInboxInquiryGroups(
      remaining,
      isInboxGroupedInquiriesEnabled,
      userId,
      isAnonymousInquiryGroupingEnabled
    );
  }, [remaining, isInboxGroupedInquiriesEnabled, userId, isAnonymousInquiryGroupingEnabled]);

  const rows = useMemo(
    () => buildInboxRows(groups, tabPages ?? [], questionGroups),
    [groups, tabPages, questionGroups]
  );

  const tipQuestionGroupKey = useMemo(() => getFirstMultiThreadQuestionGroupKey(rows), [rows]);

  const getCurrentQuery = () => {
    if (tabSelected === 'all') {
      return allInquiries;
    }

    if (tabSelected === 'open') {
      return openInquiries;
    }

    if (tabSelected === 'closed') {
      return closedInquiries;
    }

    return null;
  };

  const currentQuery = getCurrentQuery();

  useFillGroupedInboxList({
    enabled: questionGroups.length > 0 && currentQuery !== null,
    pages: tabPages,
    rowCount: rows.length,
    hasNextPage: currentQuery?.hasNextPage,
    isFetchingNextPage: currentQuery?.isFetchingNextPage ?? false,
    fetchNextPage: currentQuery?.fetchNextPage ?? noMorePages,
  });

  const { isVisible: isAnnouncementBannerVisible, dismiss: dismissAnnouncementBanner } =
    useInboxAnnouncementBanner({
      enabled: isInboxGroupedInquiriesEnabled,
      userId,
      bannerConfig: INBOX_ANNOUNCEMENT_BANNER,
    });

  const shouldShowAnnouncementBanner = isAnnouncementBannerVisible;

  if (isLoading) {
    return <MessageCardSkeleton />;
  }

  if (!rows.length) {
    const emptyTabProps = getEmptyTabProps({
      tabKey: tabSelected,
      tabSelectedKey: tabSelected,
      selectedCancerFocus: selectedCancerFocus || undefined,
    });

    return <EmptyTab key={tabSelected} {...emptyTabProps} />;
  }

  return (
    <VStack className="flex-1">
      <FlatList
        ref={flatListRef}
        className="flex-1"
        data={rows}
        keyExtractor={(row: InboxRow) => row.key}
        renderItem={({ item: row }: { item: InboxRow }) => {
          if (row.kind === 'supportThread') {
            return (
              <InboxRowShell>
                <SupportThreadCard entry={row.entry} currentTab={tabSelected} />
              </InboxRowShell>
            );
          }

          if (row.kind === 'questionGroup') {
            return (
              <QuestionGroupCard
                questionGroup={row.questionGroup}
                currentTab={tabSelected}
                showTip={row.key === tipQuestionGroupKey}
              />
            );
          }

          const inquiryGroup = row.group;

          const fullGroupInquiries =
            fullGroupInquiriesByKey.get(inquiryGroup.groupKey) ?? inquiryGroup.inquiries;

          const groupLeadInquiry = inquiryGroup.inquiries[0];

          const hasUnread = isInboxGroupedInquiriesEnabled
            ? inquiryGroup.unreadCount > 0
            : groupLeadInquiry.hasUnread;

          const firstTabConversationId =
            isInboxGroupedInquiriesEnabled && inquiryGroup.inquiries.length > 1
              ? (getFirstTabConversationId(inquiryGroup) ?? groupLeadInquiry.conversationId)
              : groupLeadInquiry.conversationId;

          const inquiryGroupCount = isInboxGroupedInquiriesEnabled
            ? (groupLeadInquiry.inquiryGroupSize ?? fullGroupInquiries.length)
            : undefined;

          const inquiryGroupExperts = isInboxGroupedInquiriesEnabled
            ? getInquiryGroupExperts(
                { ...inquiryGroup, inquiries: fullGroupInquiries },
                isAnonymousInquiryGroupingEnabled,
                userId ?? undefined
              )
            : undefined;

          const inquiryGroupStateBreakdown = isInboxGroupedInquiriesEnabled
            ? groupStateBreakdownByKey.get(inquiryGroup.groupKey)
            : undefined;

          const inquiryLabels = getUniqueInquiryLabels(fullGroupInquiries);

          const routeLabels = isInboxGroupedInquiriesEnabled
            ? getModRouteLabelsForGroup(
                { ...inquiryGroup, inquiries: fullGroupInquiries },
                userId ?? undefined
              )
            : undefined;

          return (
            <InboxRowShell legacyClassName="border-b border-outline-100">
              <InquiryCard
                {...groupLeadInquiry}
                conversationId={firstTabConversationId}
                hasUnread={hasUnread}
                labels={inquiryLabels}
                currentTab={tabSelected}
                groupedInquiriesEnabled={isInboxGroupedInquiriesEnabled}
                groupConversationIds={getInquiryConversationIds(fullGroupInquiries)}
                inquiryGroupCount={inquiryGroupCount}
                inquiryGroupVisibleCount={inquiryGroup.inquiries.length}
                inquiryGroupStateBreakdown={inquiryGroupStateBreakdown}
                groupExperts={inquiryGroupExperts}
                routeLabels={routeLabels}
              />
            </InboxRowShell>
          );
        }}
        ListHeaderComponent={
          shouldShowAnnouncementBanner ? (
            <VStack className="border-b border-outline-100">
              <InboxAnnouncementBanner
                onDismiss={() => dismissAnnouncementBanner().catch(() => undefined)}
              />
            </VStack>
          ) : null
        }
        ItemSeparatorComponent={isCardList ? null : FlatListDivider}
        refreshControl={
          !isWeb ? (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={async () => {
                setRefreshing(true);

                try {
                  if (currentQuery) {
                    await currentQuery.refetch();
                  }
                } catch (e) {
                  Sentry.captureException(e, {
                    tags: { component: 'DesktopInquiriesListView', action: 'pull-to-refresh' },
                  });
                } finally {
                  setRefreshing(false);
                }
              }}
            />
          ) : undefined
        }
        onEndReached={() => {
          if (currentQuery?.hasNextPage && !currentQuery?.isFetchingNextPage) {
            currentQuery.fetchNextPage().catch(() => undefined);
          }
        }}
        onEndReachedThreshold={LOAD_MORE_SCROLL_THRESHOLD}
        ListFooterComponent={
          <VStack className="w-full items-center py-4">
            {currentQuery?.isFetchingNextPage ? <Spinner size="large" /> : null}
          </VStack>
        }
      />
    </VStack>
  );
});
